import axios from 'axios';
import { MasonryGrid } from '@egjs/grid';
import { checkOnScreenEls } from '../../../../assets/src/js/common/on-screen';
import { showExpandable, hideExpandable, toggleExpandable } from '../../../../assets/src/js/common/expandable';
import { closeModal, openModal } from '../../../../assets/src/js/common/modals';

import '../sass/citrus-gallery.scss';

(() => {
    const $citrusGalleries = document.querySelectorAll('.citrus-gallery');

    if (!$citrusGalleries) return;

    $citrusGalleries.forEach($citrusGallery => {
        const $filterToggle = $citrusGallery.querySelector('.citrus-gallery__filter-toggle');
        const $filterContent = $citrusGallery.querySelector('.citrus-gallery__filter-content');
        const $filterClearAll = $citrusGallery.querySelector('.citrus-gallery__filter-clear-all');
        const $filterForm = $citrusGallery.querySelector('.citrus-gallery__filter-form');
        const $filterFormInputs = $filterForm?.querySelectorAll('input');

        const $typeText = $citrusGallery.querySelector('.citrus-gallery__type-text');

        const $grid = $citrusGallery.querySelector('.citrus-gallery__grid');
        const $loadMoreForm = $citrusGallery.querySelector('.citrus-gallery__load-more-form');

        const $mediaModal = $citrusGallery.querySelector('.citrus-gallery__media-modal');
        const $mediaModalCurrentCounter = $citrusGallery.querySelector('.citrus-gallery__media-modal-current-counter');
        const $mediaModalTotalCounter = $citrusGallery.querySelector('.citrus-gallery__media-modal-total-counter');

        const $mediaModalPlayBtn = $citrusGallery.querySelector('.citrus-gallery__media-modal-play-btn');
        const $mediaModalInfoBtn = $citrusGallery.querySelector('.citrus-gallery__media-modal-info-btn');
        const $mediaModalFullScreenBtn = $citrusGallery.querySelector('.citrus-gallery__media-modal-full-screen-btn');
        const $mediaModalCloseBtn = $citrusGallery.querySelector('.citrus-gallery__media-modal-close-btn');

        const $mediaModalCarousel = $citrusGallery.querySelector('.citrus-gallery__media-modal-carousel');
        const $mediaModalCarouselInner = $citrusGallery.querySelector('.citrus-gallery__media-modal-carousel-inner');

        const $mediaModalControlPrev = $citrusGallery.querySelector('.citrus-gallery__media-modal-control--prev');
        const $mediaModalControlNext = $citrusGallery.querySelector('.citrus-gallery__media-modal-control--next');

        let grid = null;
        let mediaModalCarousel = null;
        let loadingMore = true;

        const filterController = new AbortController();

        const loadMoreObserver = new IntersectionObserver(entries => {
            if (!entries) return;

            entries.forEach(entry => {
                if (!entry.isIntersecting) return;

                loadMore();
            });
        }, {
            threshold: 0
        });

        const setActiveFilter = ({
            activeSectors,
            activeTypes
        }) => {
            const $filterFormSectorInputs = $filterForm?.querySelectorAll('[name="filter_sectors[]"]');
            const $filterFormTypeInputs = $filterForm?.querySelectorAll('[name="filter_types[]"]');

            if ($filterFormSectorInputs) {
                $filterFormSectorInputs.forEach($filterFormSectorInput => {
                    $filterFormSectorInput.disabled = !$filterFormSectorInput?.checked && (!activeSectors || !activeSectors.includes($filterFormSectorInput.value));
                });
            }

            if ($filterFormTypeInputs) {
                $filterFormTypeInputs.forEach($filterFormTypeInput => {
                    $filterFormTypeInput.disabled = !$filterFormTypeInput?.checked && (!activeTypes || !activeTypes.includes($filterFormTypeInput.value));
                });
            }
        };

        const setFilterURLParams = ({
            blockAnchor,
            page,
            sectors,
            types,
        }) => {
            const url = new URL(window.location.href);
            const params = new URLSearchParams(url.search);

            params.delete('gallery_page');
            params.delete('filter_sectors[]');
            params.delete('filter_types[]');

            params.append('gallery_page', page);

            if (sectors?.length) {
                sectors.forEach(sector => {
                    params.append('filter_sectors[]', sector);
                });
            }

            if (types?.length) {
                types.forEach(type => {
                    params.append('filter_types[]', type);
                });
            }

            window.history.replaceState(null, document.title, url.pathname + (params.toString() ? '?' + params.toString() : ''));
        };

        const loadMore = () => {
            if (loadingMore && $loadMoreForm.classList.contains('citrus-gallery__load-more-form--show')) {
                setTimeout(() => {
                    loadMore();
                }, 100);
            }

            if (loadingMore || !$grid || !$loadMoreForm || !$loadMoreForm.classList.contains('citrus-gallery__load-more-form--show')) return;

            const $nonce = $loadMoreForm.querySelector('[name="citrus_load_more_citrus_gallery_wpnonce"]');
            const $postId = $loadMoreForm.querySelector('[name="post_id"]');
            const $blockAnchor = $loadMoreForm.querySelector('[name="block_anchor"]');
            const $postsPerPage = $loadMoreForm.querySelector('[name="posts_per_page"]');
            const $filterPage = $loadMoreForm.querySelector('[name="gallery_page"]');
            const $filterSectors = $loadMoreForm.querySelectorAll('[name="filter_sectors[]"]');
            const $filterTypes = $loadMoreForm.querySelectorAll('[name="filter_types[]"]');

            const nonceValue = $nonce?.value;
            const postIdValue = $postId?.value;
            const blockAnchorValue = $blockAnchor?.value;
            const postsPerPageValue = $postsPerPage?.value;
            const filterPageValue = $filterPage?.value;
            const filterSectorsValues = $filterSectors ? Array.from($filterSectors).map($filterSector => $filterSector.value) : null;
            const filterTypesValues = $filterTypes ? Array.from($filterTypes).map($filterType => $filterType.value) : null;

            loadingMore = true;

            $grid.classList.add('citrus-gallery__grid--loading');

            axios.get(GLOBALS.AJAX_URL, {
                params: {
                    action: 'citrus_ajax_get_gallery_images', 
                    nonce: nonceValue,
                    post_id: postIdValue,
                    block_anchor: blockAnchorValue,
                    posts_per_page: postsPerPageValue,
                    page: filterPageValue,
                    sectors: filterSectorsValues,
                    types: filterTypesValues,
                },
                signal: filterController.signal,
            }).then(response => {
                if (!response?.data?.success) return;

                if (response?.data?.images) {
                    $grid.insertAdjacentHTML('beforeend', response.data.images);

                    setFilterURLParams({
                        blockAnchor: blockAnchorValue,
                        page: filterPageValue,
                        sectors: filterSectorsValues,
                        types: filterTypesValues,
                    });
                }

                if (response?.data?.page && $filterPage) {
                    $filterPage.value = parseInt(response.data.page) + 1;
                }

                initGalleryCards();

                if (grid) {
                    grid.syncElements();
                }

                // Show/hide load more button
                if (response?.data?.found_posts >= response?.data?.post_count || !response?.data?.images) {
                    $loadMoreForm.classList.remove('citrus-gallery__load-more-form--show');
                } else {
                    $loadMoreForm.classList.add('citrus-gallery__load-more-form--show');
                }
            }).finally(() => {
                $grid.classList.remove('citrus-gallery__grid--loading');

                loadMore();
            });
        };

        const mediaModalAutoplayStop = () => {
            mediaModalCarousel.autoplay.stop();
            $mediaModalPlayBtn.classList.remove('citrus-gallery__media-modal-play-btn--pause');
        };

        const mediaModalAutoplayStart = () => {
            mediaModalCarousel.autoplay.start();
            $mediaModalPlayBtn.classList.add('citrus-gallery__media-modal-play-btn--pause');
        };

        const submitFilterForm = () => {
            if (!$grid || !$filterForm) return;

            const $nonce = $filterForm.querySelector('[name="citrus_load_more_citrus_gallery_wpnonce"]');
            const $postId = $filterForm.querySelector('[name="post_id"]');
            const $blockAnchor = $filterForm.querySelector('[name="block_anchor"]');
            const $postsPerPage = $filterForm.querySelector('[name="posts_per_page"]');
            const $filterSectors = $filterForm.querySelectorAll('[name="filter_sectors[]"]');
            const $filterTypes = $filterForm.querySelectorAll('[name="filter_types[]"]');

            const $loadMoreFilterPage = $loadMoreForm.querySelector('[name="gallery_page"]');

            const $loadMoreFilterFields = $loadMoreForm.querySelectorAll('[name="filter_sectors[]"]');

            const nonceValue = $nonce?.value;
            const postIdValue = $postId?.value;
            const blockAnchorValue = $blockAnchor?.value;
            const postsPerPageValue = $postsPerPage?.value;
            const filterSectorsValues = $filterSectors ? Array.from($filterSectors).filter($filterSector => $filterSector.checked).map($filterSector => $filterSector.value) : null;
            const filterTypesValues = $filterTypes ? Array.from($filterTypes).filter($filterType => $filterType.checked).map($filterType => $filterType.value) : null;

            if ($loadMoreFilterFields) {
                $loadMoreFilterFields.forEach($loadMoreFilterField => {
                    $loadMoreFilterField.remove();
                });
            }

            filterSectorsValues?.forEach(sector => {
                $loadMoreForm.insertAdjacentHTML('afterbegin', `<input type="hidden" name="filter_sectors[]" value="${sector}" autocomplete="off">`);
            });

            filterTypesValues?.forEach(type => {
                $loadMoreForm.insertAdjacentHTML('afterbegin', `<input type="hidden" name="filter_types[]" value="${type}" autocomplete="off">`);
            });

            loadingMore = true;

            $grid.classList.add('citrus-gallery__grid--loading');

            axios.get(GLOBALS.AJAX_URL, {
                params: {
                    action: 'citrus_ajax_get_gallery_images', 
                    nonce: nonceValue,
                    post_id: postIdValue,
                    block_anchor: blockAnchorValue,
                    posts_per_page: postsPerPageValue,
                    page: 1,
                    sectors: filterSectorsValues,
                    types: filterTypesValues,
                },
                signal: filterController.signal,
            }).then(response => {
                if (!response?.data?.success) return;

                $grid.innerHTML = response?.data?.images ?? '';

                initGalleryCards();

                setFilterURLParams({
                    blockAnchor: blockAnchorValue,
                    page: 1,
                    sectors: filterSectorsValues,
                    types: filterTypesValues,
                });

                setActiveFilter({
                    activeSectors: response?.data?.filters?.active_sectors ?? [],
                    activeTypes: response?.data?.filters?.active_types ?? [],
                });

                if ($loadMoreFilterPage) {
                    $loadMoreFilterPage.value = 2;
                }

                $mediaModalCurrentCounter.innerHTML = 1;
                $mediaModalTotalCounter.innerHTML = response?.data?.post_count;

                if (response?.data?.media_modal_carousel_slides) {
                    $mediaModalCarouselInner.innerHTML = response.data.media_modal_carousel_slides;
                }

                initMediaModalCarousel();

                const $images = $grid.querySelectorAll('.citrus-gallery-card img');

                let loadedComplete = 0;

                const loadComplete = () => {
                    loadedComplete++;

                    if (loadedComplete !== $images?.length) return;

                    checkOnScreenEls();

                    loadingMore = false;
                };

                if ($images) {
                    $images.forEach($image => {
                        if ($image?.complete) {
                            loadComplete();

                            return;
                        }

                        $image.addEventListener('load', () => {
                            loadComplete();
                        });

                        $image.addEventListener('error', () => {
                            loadComplete();
                        });
                    });
                }

                if (grid) {
                    grid.syncElements();
                }

                if ($typeText && response?.data?.gallery_type_text) {
                    $typeText.classList.add('citrus-gallery__type-text--show');

                    $typeText.innerHTML = `<div class="on-screen">${response.data.gallery_type_text.map(gallery_type_text => {
                        return `<div class="fade-slide-in">${gallery_type_text}</div>`;
                    }).toString()}</div>`;
                }

                if ($typeText && !response?.data?.gallery_type_text) {
                    $typeText.classList.remove('citrus-gallery__type-text--show');
                    
                    $typeText.innerHTML = '';
                }

                // Show/hide load more button
                if (response?.data?.found_posts >= response?.data?.post_count) {
                    $loadMoreForm.classList.remove('citrus-gallery__load-more-form--show');
                } else {
                    $loadMoreForm.classList.add('citrus-gallery__load-more-form--show');
                }
            }).finally(() => {
                $grid.classList.remove('citrus-gallery__grid--loading');
            });
        };

        const initFilterClearAll = () => {
            if (!$filterClearAll) return;

            $filterClearAll.addEventListener('submit', e => {
                e.preventDefault();

                $filterFormInputs.forEach($filterFormInput => {
                    $filterFormInput.checked = false;
                });

                submitFilterForm();
            })
        };

        const initFilter = () => {
            if (!$filterToggle || !$filterContent) return;

            $filterToggle.addEventListener('click', () => {
                if ($filterContent.classList.contains('citrus-expandable--hide')) {
                    $filterToggle.classList.add('citrus-gallery__filter-toggle--visible');

                    showExpandable($filterContent);
                } else {
                    $filterToggle.classList.remove('citrus-gallery__filter-toggle--visible');
                    
                    hideExpandable($filterContent);
                }
            });

            $filterContent.addEventListener('expandableafterclose', () => {
                checkOnScreenEls();
            });

            $filterForm.addEventListener('submit', e => {
                e.preventDefault();

                submitFilterForm();
            });

            if ($filterFormInputs) {
                $filterFormInputs.forEach($filterFormInput => {
                    $filterFormInput.addEventListener('change', () => {
                        submitFilterForm();
                    });
                });
            }
        };

        const initImagesGrid = () => {
            if (!$grid) return;

            grid = new MasonryGrid($grid, {
                align: 'start',
            });

            grid.renderItems();

            grid.on('renderComplete', () => {
                const $galleryCards = $citrusGallery.querySelectorAll('.citrus-gallery-card');

                if ($galleryCards) {
                    $galleryCards.forEach($galleryCard => {
                        $galleryCard.classList.add('on-screen');
                    });
                }

                checkOnScreenEls();
            });

            loadingMore = false;
        };

        const initLoadMoreForm = () => {
            if (!$loadMoreForm) return;

            $loadMoreForm.addEventListener('submit', e => {
                e.preventDefault();

                loadMore();
            });

            loadMoreObserver.observe($loadMoreForm);
        };

        const initMediaModal = () => {
            if (!$mediaModal) return;

            $mediaModal.addEventListener('modalclose', () => {
                if (document.fullscreenElement && document.exitFullscreen) {
                    document.exitFullscreen();
                }

                mediaModalAutoplayStop();
            });

            $mediaModal.addEventListener('modalafterclose', () => {
                const $imageDescriptions = $citrusGallery.querySelectorAll('.citrus-gallery-modal-slide__description');
                
                if ($imageDescriptions) {
                    $imageDescriptions.forEach($imageDescription => {
                        hideExpandable($imageDescription);
                    });
                }
            });
        };

        const initMediaModalCarousel = () => {
            if (!$mediaModalCarousel) return;

            if (mediaModalCarousel) {
                mediaModalCarousel.destroy();
            }

            mediaModalCarousel = new Swiper($mediaModalCarousel, {
                effect: 'fade',
                fadeEffect: {
                    crossFade: false
                },
                keyboard: {
                    enabled: true,
                    onlyInViewport: true,
                },
                loop: true,
                autoplay: {
                    delay: 2000,
                },
                speed: 1000,
                navigation: {
                    nextEl: $mediaModalControlNext ?? null,
                    prevEl: $mediaModalControlPrev ?? null,
                },
                allowTouchMove: false,
            });

            mediaModalAutoplayStop();

            mediaModalCarousel.on('realIndexChange', () => {
                $mediaModalCurrentCounter.innerHTML = mediaModalCarousel?.realIndex + 1;
            });

            mediaModalCarousel.on('sliderMove', () => {
                if (!$mediaModalPlayBtn) return;

                $mediaModalPlayBtn.classList.remove('citrus-gallery__media-modal-play-btn--pause');
            });

            const $imageDescriptions = $citrusGallery.querySelectorAll('.citrus-gallery-modal-slide__description');
            const $descriptionCloseButtons = $citrusGallery.querySelectorAll('.citrus-gallery-modal-slide__description-close');

            if ($imageDescriptions && $descriptionCloseButtons) {
                $descriptionCloseButtons.forEach($descriptionCloseButton => {
                    $descriptionCloseButton.addEventListener('click', () => {
                        $imageDescriptions.forEach($imageDescription => {
                            hideExpandable($imageDescription);
                        });
                    });
                });
            }

            if ($mediaModalControlNext && $mediaModalPlayBtn) {
                $mediaModalControlNext.addEventListener('click', () => {
                    $mediaModalPlayBtn.classList.remove('citrus-gallery__media-modal-play-btn--pause');
                })
            }

            if ($mediaModalControlPrev && $mediaModalPlayBtn) {
                $mediaModalControlPrev.addEventListener('click', () => {
                    $mediaModalPlayBtn.classList.remove('citrus-gallery__media-modal-play-btn--pause');
                })
            }
        };

        const initMediaModalControls = () => {
            if ($mediaModalPlayBtn) {
                $mediaModalPlayBtn.addEventListener('click', () => {
                    if (mediaModalCarousel?.autoplay?.running) {
                        mediaModalAutoplayStop();
                    } else {
                        mediaModalAutoplayStart();
                    }
                });
            }

            if ($mediaModalInfoBtn) {
                $mediaModalInfoBtn.addEventListener('click', () => {
                    const $imageDescriptions = $citrusGallery.querySelectorAll('.citrus-gallery-modal-slide__description');

                    if (!$imageDescriptions) return;

                    $imageDescriptions.forEach($imageDescription => {
                        toggleExpandable($imageDescription);
                    });
                });
            }

            if ($mediaModalFullScreenBtn) {
                $mediaModalFullScreenBtn.addEventListener('click', () => {
                    if (!document.fullscreenElement && $mediaModal.requestFullscreen) {
                        $mediaModal.requestFullscreen();
                    }

                    if (document.fullscreenElement && document.exitFullscreen) {
                        document.exitFullscreen();
                    }
                });
            }

            if ($mediaModalCloseBtn) {
                $mediaModalCloseBtn.addEventListener('click', () => {
                    closeModal($mediaModal);
                });
            }
        };

        const initGalleryCards = () => {
            const $galleryCards = $citrusGallery.querySelectorAll('.citrus-gallery-card');

            if (!$galleryCards) return;

            $galleryCards.forEach($galleryCard => {
                if ($galleryCard.classList.contains('citrus-gallery-card--initiated')) return;

                const $galleryCardButton = $galleryCard.querySelector('.citrus-gallery-card__inner');

                const { mediaModalIndex } = $galleryCardButton.dataset;

                $galleryCardButton.addEventListener('click', () => {
                    if (mediaModalCarousel) {
                        mediaModalCarousel.slideToLoop(mediaModalIndex, 1);
                    }
    
                    openModal($mediaModal);
                });

                $galleryCardButton.classList.add('citrus-gallery-card--initiated');
            });
        };

        const initCarousel = () => {
            const swiperScript = document.querySelector('#citrus-theme-swiper-js');

            if (!swiperScript) return;

            swiperScript.addEventListener('load', () => {
                initMediaModalCarousel();
            });
        };

        initImagesGrid();
        initLoadMoreForm();
        initFilterClearAll();
        initFilter();
        initMediaModal();
        initCarousel();
        initMediaModalControls();
        initGalleryCards();
    });
})();
